(function (root) {
  'use strict';

  const VERSION = 'elmuno-gradient-name-v1';
  const FONT_FAMILY = 'Elmuno Alex Brush';
  const FONT_SHA256 = 'df702038d8e27797230c77959c139eeea38cac0caf53e19ea5b513d3b0d3362d';
  // Shopify serves an equivalent WOFF2 for the uploaded TTF. Both exact byte
  // hashes were verified against all 617 glyph outlines and shaping tables.
  const VERIFIED_FONT_HASHES = new Set([
    FONT_SHA256,
    '86a9e5895e88da42a028dca1fb4c72065ac46822a4831d09fd34cafa85c43799'
  ]);
  const WIDTH = 2000;
  const HEIGHT = 3400;
  // Canva's 145 pt equals 193 1/3 pixels on this print canvas.
  const BASE_FONT_SIZE = 145 * 4 / 3;
  const X = 1000;
  // Calibrated against the actual Canva-exported Elmuno letter pixels.
  const BASELINE = 2920;
  const MAX_HALF_INK_WIDTH = 500;
  const MAX_GRAPHEMES = 20;
  const COLOR = '#000000';
  const fontLoads = new Map();
  // Unicode cmap coverage read directly from the bundled, hash-checked font.
  const FONT_RANGES = [[13,13],[32,126],[160,328],[330,382],[399,399],[402,402],[416,417],[431,432],[453,455],[457,458],[460,462],[486,487],[490,491],[506,539],[554,557],[560,563],[567,567],[601,601],[697,698],[700,700],[710,711],[728,733],[768,772],[774,780],[783,783],[785,786],[795,795],[803,804],[806,808],[814,814],[817,817],[821,821],[960,960],[7808,7813],[7838,7838],[7840,7929],[8208,8208],[8211,8212],[8216,8218],[8220,8222],[8224,8226],[8230,8230],[8240,8240],[8242,8243],[8249,8250],[8260,8260],[8274,8274],[8308,8308],[8353,8353],[8355,8356],[8358,8359],[8361,8361],[8363,8365],[8369,8370],[8373,8373],[8377,8378],[8380,8381],[8467,8467],[8470,8470],[8482,8482],[8486,8486],[8494,8494],[8706,8706],[8710,8710],[8719,8719],[8721,8722],[8725,8725],[8729,8730],[8734,8734],[8747,8747],[8776,8776],[8800,8800],[8804,8805],[9674,9674],[10216,10217],[63743,63743],[64257,64258]];

  function validateName(value) {
    const name = typeof value === 'string' ? value.normalize('NFC').trim() : '';
    const graphemeCount = root.Intl && typeof root.Intl.Segmenter === 'function'
      ? Array.from(new root.Intl.Segmenter('de', { granularity: 'grapheme' }).segment(name)).length
      : Array.from(name).length;
    let error = '';
    if (!name) error = 'Bitte gib deinen Namen ein.';
    else if (graphemeCount > MAX_GRAPHEMES) error = 'Dein Name darf höchstens 20 Zeichen haben.';
    else if (!/^[\p{L}\p{M} '\u2019\u2010-]+$/u.test(name) || !/\p{L}/u.test(name)) {
      error = 'Bitte verwende nur Buchstaben, Leerzeichen, Bindestriche oder Apostrophe.';
    } else if (Array.from(name).some(character => {
      const code = character.codePointAt(0);
      return !FONT_RANGES.some(range => code >= range[0] && code <= range[1]);
    })) error = 'Ein Zeichen deines Namens ist in dieser Schrift leider nicht verfügbar.';
    return { valid: !error, name, error, graphemeCount };
  }

  async function responseBytes(url, kind) {
    const response = await root.fetch(url, { mode: 'cors', credentials: 'omit', cache: 'force-cache' });
    if (!response.ok) throw new Error(kind + ' konnte nicht geladen werden. Bitte versuche es erneut.');
    return response;
  }

  async function loadFont(url) {
    if (!url || typeof root.FontFace !== 'function' || !root.document.fonts || !root.crypto?.subtle) {
      throw new Error('Die Namensvorschau wird von diesem Browser gerade nicht unterstützt.');
    }
    if (!fontLoads.has(url)) {
      const pending = (async function () {
        const bytes = await (await responseBytes(url, 'Die Schrift')).arrayBuffer();
        const digest = Array.from(new Uint8Array(await root.crypto.subtle.digest('SHA-256', bytes)), byte => byte.toString(16).padStart(2, '0')).join('');
        if (!VERIFIED_FONT_HASHES.has(digest)) throw new Error('Die passende Schrift konnte nicht bestätigt werden. Bitte versuche es erneut.');
        const font = new root.FontFace(FONT_FAMILY, bytes, { style: 'normal', weight: '400' });
        await font.load();
        root.document.fonts.add(font);
        await root.document.fonts.load('400 ' + BASE_FONT_SIZE + 'px "' + FONT_FAMILY + '"', 'Elmuno');
        if (!root.document.fonts.check('400 ' + BASE_FONT_SIZE + 'px "' + FONT_FAMILY + '"', 'Elmuno')) {
          throw new Error('Die passende Schrift ist noch nicht bereit. Bitte versuche es erneut.');
        }
        return { font, digest };
      }());
      fontLoads.set(url, pending);
      pending.catch(function () { fontLoads.delete(url); });
    }
    return fontLoads.get(url);
  }

  async function loadBackground(url) {
    if (!url) throw new Error('Das Farbverlaufsdesign fehlt.');
    const blob = await (await responseBytes(url, 'Das Farbverlaufsdesign')).blob();
    if (typeof root.createImageBitmap === 'function') return root.createImageBitmap(blob);
    const objectUrl = root.URL.createObjectURL(blob);
    try {
      return await new Promise(function (resolve, reject) {
        const image = new root.Image();
        image.onload = function () { resolve(image); };
        image.onerror = function () { reject(new Error('Das Farbverlaufsdesign konnte nicht geöffnet werden.')); };
        image.src = objectUrl;
      });
    } finally { root.URL.revokeObjectURL(objectUrl); }
  }

  function fitName(context, name) {
    context.textAlign = 'center';
    context.textBaseline = 'alphabetic';
    context.direction = 'ltr';
    if ('fontKerning' in context) context.fontKerning = 'normal';
    if ('letterSpacing' in context) context.letterSpacing = '0px';
    let fontSize = BASE_FONT_SIZE;
    let metrics;
    for (let attempt = 0; attempt < 12; attempt++) {
      context.font = '400 ' + fontSize + 'px "' + FONT_FAMILY + '"';
      metrics = context.measureText(name);
      if (![metrics.actualBoundingBoxLeft, metrics.actualBoundingBoxRight, metrics.actualBoundingBoxAscent, metrics.actualBoundingBoxDescent].every(Number.isFinite)) {
        throw new Error('Die Position deines Namens konnte nicht sicher berechnet werden.');
      }
      const extent = Math.max(metrics.actualBoundingBoxLeft, metrics.actualBoundingBoxRight);
      if (extent <= MAX_HALF_INK_WIDTH) break;
      fontSize *= (MAX_HALF_INK_WIDTH - 1) / extent;
    }
    const bounds = { left: X - metrics.actualBoundingBoxLeft, right: X + metrics.actualBoundingBoxRight, top: BASELINE - metrics.actualBoundingBoxAscent, bottom: BASELINE + metrics.actualBoundingBoxDescent };
    if (bounds.left < X - MAX_HALF_INK_WIDTH || bounds.right > X + MAX_HALF_INK_WIDTH || bounds.top < 2650 || bounds.bottom > 3100) {
      throw new Error('Dieser Name passt nicht in den vorgesehenen Bereich. Bitte kürze ihn etwas.');
    }
    return { fontSize, bounds };
  }

  async function compose(options) {
    const validated = validateName(options?.name);
    if (!validated.valid) throw new Error(validated.error);
    const loadedFont = await loadFont(options.fontUrl);
    const background = await loadBackground(options.backgroundUrl);
    try {
      if (background.width !== WIDTH || background.height !== HEIGHT) {
        throw new Error('Das Farbverlaufsdesign hat nicht das benötigte Druckformat.');
      }
      const canvas = root.document.createElement('canvas');
      canvas.width = WIDTH;
      canvas.height = HEIGHT;
      const context = canvas.getContext('2d', { alpha: false, colorSpace: 'srgb' });
      if (!context) throw new Error('Die Namensvorschau konnte nicht gestartet werden.');
      context.drawImage(background, 0, 0);
      const layout = fitName(context, validated.name);
      context.fillStyle = COLOR;
      // Do not use fillText's maxWidth: it can distort the letterforms horizontally.
      context.fillText(validated.name, X, BASELINE);
      const blob = await new Promise(function (resolve, reject) {
        canvas.toBlob(function (result) {
          if (result) resolve(result);
          else reject(new Error('Deine Druckdatei konnte nicht erstellt werden. Bitte versuche es erneut.'));
        }, 'image/png');
      });
      if (blob.type !== 'image/png') throw new Error('Deine Druckdatei konnte nicht im richtigen Format erstellt werden.');
      return {
        file: new root.File([blob], 'elmuno-name-druckdatei.png', { type: 'image/png' }),
        typography: { fontFamily: 'Alex Brush', fontSize: layout.fontSize, x: X, baseline: BASELINE, color: COLOR, version: VERSION, name: validated.name, inkBounds: layout.bounds, fontSha256: loadedFont.digest, fontSourceSha256: FONT_SHA256 }
      };
    } finally { if (typeof background.close === 'function') background.close(); }
  }

  const api = Object.freeze({ compose, validateName });
  root.ElmunoGradientArtwork = api;
  if (typeof module === 'object' && module.exports) module.exports = api;
}(typeof window !== 'undefined' ? window : globalThis));
